import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Snackbar,
  Button,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import reportService from '../services/reportService';
import * as mammoth from 'mammoth';
import ClientSelector from '../components/ClientSelector';

interface Report {
  name: string;
  type: string;
  s3_key: string;
}

interface ExcelPreviewData {
  columns: string[];
  data: (string | number)[][];
}

const ReportsPage: React.FC = () => {
  const [reports, setReports] = useState<Report[]>([]);
  const [loadingReports, setLoadingReports] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedClient, setSelectedClient] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [reportHtml, setReportHtml] = useState<string>('');
  const [reportBlob, setReportBlob] = useState<Blob | null>(null);
  const [isGeneratingReport, setIsGeneratingReport] = useState<boolean>(false);
  const [availableReportTypes, setAvailableReportTypes] = useState<string[]>([]);
  const [selectedReportType, setSelectedReportType] = useState<string>('');
  const [selectedGranularity, setSelectedGranularity] = useState<string>('');
  const [excelPreviewData, setExcelPreviewData] = useState<ExcelPreviewData | null>(null);
  const [loadingPreview, setLoadingPreview] = useState<boolean>(false);

  const fetchReports = useCallback(async () => {
    if (selectedClient) {
      try {
        setLoadingReports(true);
        setError(null);
        const response = await reportService.listReports(selectedClient);
        setReports(response.data);
      } catch (err) {
        setError('Error al cargar los reportes.');
        console.error(err);
      } finally {
        setLoadingReports(false);
      }
    } else {
      setReports([]);
    }
  }, [selectedClient]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  useEffect(() => {
    const fetchAvailableReportTypes = async () => {
      if (selectedClient) {
        try {
          const response = await reportService.getAvailableReportTypes(selectedClient);
          setAvailableReportTypes(response.data);
          setSelectedReportType('');
        } catch (err) {
          console.error("Error fetching available report types", err);
          setAvailableReportTypes([]);
        }
      } else {
        setAvailableReportTypes([]);
        setSelectedReportType('');
      }
    };
    fetchAvailableReportTypes();
  }, [selectedClient]);

  const handleDownload = async (fileName: string) => {
    try {
      setSuccessMessage(`Descargando ${fileName}...`);
      const response = await reportService.downloadReport(fileName);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setSuccessMessage('Reporte descargado con éxito.');
    } catch (err) {
      setError('Error al descargar el reporte.');
      console.error(err);
    }
  };

  const handleGenerateReport = async () => {
    if (!selectedClient || !selectedReportType || !selectedGranularity) return;
    setIsGeneratingReport(true);
    setError(null);
    setReportHtml('');
    setReportBlob(null);
    setExcelPreviewData(null);

    try {
      const response = await reportService.generateDocxReport(selectedClient, selectedReportType, selectedGranularity);
      // The response now contains the s3_key of the generated report
      const { s3_key } = response.data;

      if (s3_key && typeof s3_key === 'string') {
        // Iniciar la descarga automáticamente
        await handleDownload(s3_key);
        
        // Opcional: también mostrar la vista previa
        await handlePreviewDocx(s3_key);
      } else {
        setError("El backend no devolvió una ruta de archivo válida para el informe.");
        console.error("Respuesta inválida del backend al generar informe:", response.data);
      }
      
      // Refrescar la lista de reportes
      fetchReports();
    } catch (err) {
      setError('Error al generar el informe.');
      console.error(err);
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const handleDownloadGeneratedReport = () => {
    if (reportBlob) {
      const url = window.URL.createObjectURL(reportBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `informe_analisis_${selectedClient}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    }
  };

  const handleClientChange = (clientName: string | null) => {
    setSelectedClient(clientName);
    setReports([]);
    setReportHtml('');
    setReportBlob(null);
    setAvailableReportTypes([]);
    setSelectedReportType('');
    setSelectedGranularity('');
    setExcelPreviewData(null);
  };

  const handleDelete = async (fileName: string) => {
    try {
      setSuccessMessage(`Eliminando ${fileName}...`);
      await reportService.deleteReport(fileName);
      setSuccessMessage('Reporte eliminado con éxito.');
      fetchReports();
    } catch (err) {
      setError('Error al eliminar el reporte.');
      console.error(err);
    }
  };

  const handleRegenerate = async (reportType: string) => {
    setSelectedReportType(reportType);
    await handleGenerateReport();
  };

  const handlePreview = async (fileName: string) => {
    setLoadingPreview(true);
    setError(null);
    setReportHtml('');
    setExcelPreviewData(null);
    try {
      const response = await reportService.previewReport(fileName);
      setExcelPreviewData(response.data);
    } catch (err) {
      setError('Error al previsualizar el reporte.');
      console.error(err);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handlePreviewDocx = async (fileName: string) => {
    setLoadingPreview(true);
    setError(null);
    setReportHtml('');
    setExcelPreviewData(null);
    try {
      const response = await reportService.downloadReport(fileName);
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      const arrayBuffer = await blob.arrayBuffer();
      const result = await mammoth.convertToHtml({ arrayBuffer: arrayBuffer });
      setReportHtml(result.value);
      setReportBlob(blob);
    } catch (err) {
      setError('Error al previsualizar el informe.');
      console.error(err);
    } finally {
      setLoadingPreview(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
      {/* === SECCIÓN SUPERIOR FIJA (CONTROLES) === */}
      <Box sx={{ p: 3, flexShrink: 0 }}>
        <Typography variant="h4" gutterBottom>Reportes</Typography>
        <ClientSelector selectedClient={selectedClient} onClientChange={handleClientChange} />
        {error && <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        <Box sx={{ display: 'flex', gap: 2, mt: 2, alignItems: 'center' }}>
          <FormControl sx={{ minWidth: 240 }} disabled={!selectedClient || availableReportTypes.length === 0}>
            <InputLabel id="report-type-select-label">Tipo de Informe</InputLabel>
            <Select
              labelId="report-type-select-label"
              id="report-type-select"
              value={selectedReportType}
              label="Tipo de Informe"
              onChange={(e) => setSelectedReportType(e.target.value as string)}
            >
              {availableReportTypes.map((type) => (
                <MenuItem key={type} value={type}>{type}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 180 }} disabled={!selectedClient}>
            <InputLabel id="granularity-select-label">Agrupar Por</InputLabel>
            <Select
              labelId="granularity-select-label"
              value={selectedGranularity}
              label="Agrupar Por"
              onChange={(e) => setSelectedGranularity(e.target.value as string)}
            >
              <MenuItem value="Anual">Año</MenuItem>
              <MenuItem value="Mensual">Mes</MenuItem>
              <MenuItem value="Diaria">Día</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            onClick={handleGenerateReport}
            disabled={!selectedClient || !selectedReportType || !selectedGranularity || isGeneratingReport}
          >
            {isGeneratingReport ? <CircularProgress size={24} /> : 'Generar Informe'}
          </Button>
        </Box>
      </Box>

      {/* === SECCIÓN INFERIOR SCROLLABLE (CONTENIDO) === */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 3, pb: 3, minWidth: 0 }}>
        {loadingPreview && <CircularProgress />}

        {excelPreviewData && (
          <Paper sx={{ mt: 2, width: '100%', overflow: 'hidden', maxHeight: 'calc(100vh - 400px)', display: 'flex', flexDirection: 'column' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" p={2} flexShrink={0}>
              <Typography variant="h5">Vista Previa de Excel</Typography>
              <IconButton onClick={() => setExcelPreviewData(null)}><CloseIcon /></IconButton>
            </Box>
            <Box sx={{ overflow: 'auto', flexGrow: 1, minWidth: 0 }}>
              <TableContainer>
                <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {excelPreviewData.columns.map((col) => (<TableCell key={col} sx={{ backgroundColor: 'grey.200' }}>{col}</TableCell>))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {excelPreviewData.data.map((row, rowIndex) => (
                    <TableRow key={rowIndex}>
                      {row.map((cell, cellIndex) => (<TableCell key={cellIndex}>{cell}</TableCell>))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            </Box>
          </Paper>
        )}

        {reportHtml && (
          <Paper sx={{ mt: 2, width: '100%', overflow: 'hidden', maxHeight: 'calc(100vh - 400px)', display: 'flex', flexDirection: 'column' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" p={2} flexShrink={0}>
              <Typography variant="h5">Vista Previa del Informe</Typography>
              <div>
                <Button variant="outlined" onClick={handleDownloadGeneratedReport} disabled={!reportBlob} sx={{ mr: 1 }}>
                  Descargar .docx
                </Button>
                <IconButton onClick={() => setReportHtml('')}><CloseIcon /></IconButton>
              </div>
            </Box>
            <Box
              sx={{ overflow: 'auto', flexGrow: 1, p: 2, backgroundColor: '#fff', color: '#000' }}
              dangerouslySetInnerHTML={{ __html: reportHtml }}
            />
          </Paper>
        )}

        <Typography variant="h5" sx={{ mt: 4, mb: 2 }}>Reportes Generados</Typography>
        {loadingReports ? (
          <CircularProgress />
        ) : (
          <List>
            {reports.length > 0 ? (
              reports.map((report) => (
                <ListItem
                  key={report.name}
                  secondaryAction={
                    <>
                      {report.type === 'Excel' && (
                        <IconButton edge="end" aria-label="preview" onClick={() => handlePreview(report.s3_key)}>
                          <VisibilityIcon />
                        </IconButton>
                      )}
                      {report.type === 'Informe de Facturación' && (
                        <IconButton edge="end" aria-label="preview" onClick={() => handlePreviewDocx(report.s3_key)}>
                          <VisibilityIcon />
                        </IconButton>
                      )}
                      <IconButton edge="end" aria-label="regenerate" onClick={() => handleRegenerate(report.type)}>
                        <RefreshIcon />
                      </IconButton>
                      <IconButton edge="end" aria-label="download" onClick={() => handleDownload(report.s3_key)}>
                        <DownloadIcon />
                      </IconButton>
                      <IconButton edge="end" aria-label="delete" onClick={() => handleDelete(report.s3_key)}>
                        <DeleteIcon />
                      </IconButton>
                    </>
                  }
                >
                  <ListItemText primary={report.name} secondary={report.type} />
                </ListItem>
              ))
            ) : (
              <Box textAlign="center" mt={4}>
                <Typography>{selectedClient ? 'No hay reportes para este cliente.' : 'Seleccione un cliente para ver sus reportes.'}</Typography>
                {selectedClient && (
                  <Button
                    variant="contained"
                    sx={{ mt: 2 }}
                    onClick={() => { /* Logic to open generation controls */ }}
                  >
                    Generar Nuevo Reporte
                  </Button>
                )}
              </Box>
            )}
          </List>
        )}
      </Box>
      <Snackbar open={!!successMessage} autoHideDuration={4000} onClose={() => setSuccessMessage(null)} message={successMessage} />
    </Box>
  );
};

export default ReportsPage;